import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Edit, Eye, EyeOff, Globe, FileText, ChevronRight, Save, X, Trash2, Plus } from 'lucide-react';

const PAGES = [
  {
    id: 'landing', title: 'Page d\'accueil', slug: '/', published: true, lastEdit: '2026-01-20',
    sections: [
      { key: 'hero_title', label: 'Titre principal', value: 'Votre aidé mérite une parenthèse d\'éclat. Vous aussi.' },
      { key: 'hero_subtitle', label: 'Sous-titre', value: 'Séjours de répit coordonnés France-Maroc — Marrakech & Agadir.' },
      { key: 'hero_cta', label: 'Bouton principal', value: 'Simuler mon éligibilité' },
      { key: 'stats_families', label: 'Stat : Familles', value: '120+ familles accompagnées' },
      { key: 'stats_partners', label: 'Stat : Partenaires', value: '8 partenaires certifiés' },
    ]
  },
  {
    id: 'about', title: 'Comment ça marche', slug: '/comment-ca-marche', published: true, lastEdit: '2026-01-15',
    sections: [
      { key: 'intro', label: 'Introduction', value: 'M-CareLink coordonne chaque étape du séjour de répit, de la demande au retour.' },
      { key: 'step1', label: 'Étape 1', value: 'Simulez votre éligibilité en 2 minutes.' },
      { key: 'step2', label: 'Étape 2', value: 'Notre équipe vous contacte sous 48h.' },
      { key: 'step3', label: 'Étape 3', value: 'Nous coordonnons l\'ensemble du séjour.' },
    ]
  },
  {
    id: 'stays', title: 'Nos séjours', slug: '/nos-sejours', published: true, lastEdit: '2026-01-18',
    sections: [
      { key: 'page_title', label: 'Titre de la page', value: 'Nos Séjours de Répit' },
      { key: 'page_desc', label: 'Description', value: 'Découvrez nos formules de séjours adaptées à chaque dyade aidant-aidé.' },
    ]
  },
  {
    id: 'partners', title: 'Pour les partenaires', slug: '/partenaires', published: true, lastEdit: '2026-02-01',
    sections: [
      { key: 'hero', label: 'Titre', value: 'Rejoignez le réseau M-CareLink' },
      { key: 'criteria', label: 'Critères', value: 'Accessibilité PMR, personnel formé, cadre thérapeutique.' },
    ]
  },
  {
    id: 'legal', title: 'Mentions légales', slug: '/mentions-legales', published: true, lastEdit: '2025-12-10',
    sections: [
      { key: 'company', label: 'Société', value: 'M-CareLink SAS — 123 rue Example, 75001 Paris' },
      { key: 'rcs', label: 'RCS', value: 'RCS Paris 123 456 789' },
    ]
  },
];

export default function CMSModule() {
  const [pages, setPages] = useState(PAGES);
  const [editing, setEditing] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [dirty, setDirty] = useState(false);

  const startEdit = (page) => {
    const vals = {};
    page.sections.forEach(s => { vals[s.key] = s.value; });
    setEditValues(vals);
    setEditing(page);
    setDirty(false);
  };

  const saveEdit = () => {
    setPages(pages.map(p => p.id === editing.id ? {
      ...p,
      lastEdit: new Date().toISOString().split('T')[0],
      sections: p.sections.map(s => ({ ...s, value: editValues[s.key] ?? s.value }))
    } : p));
    setEditing(null);
    setDirty(false);
    toast.success('Page sauvegardée avec succès');
  };

  const togglePublish = (id) => {
    setPages(pages.map(p => p.id === id ? { ...p, published: !p.published } : p));
    toast.success('Statut de publication mis à jour');
  };

  const removePage = (id) => {
    const page = pages.find(p => p.id === id);
    if (!window.confirm(`Supprimer la page « ${page.title} » ?`)) return;
    setPages(pages.filter(p => p.id !== id));
    toast.success('Page supprimée');
  };

  return (
    <div className="p-6 space-y-6" data-testid="cms-module">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-primary">CMS Pages</h2>
        <p className="text-sm text-muted-foreground font-body">Éditez le contenu des pages sans toucher au code</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: 'Pages publiées', value: pages.filter(p => p.published).length, Icon: Globe, color: '#22863A' },
          { label: 'Pages total', value: pages.length, Icon: FileText, color: '#5A3B31' },
          { label: 'Sections modifiables', value: pages.reduce((a, p) => a + p.sections.length, 0), Icon: Edit, color: '#F47B20' },
        ].map(({ label, value, Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl p-5 border border-border shadow-sm flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: color + '18' }}>
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            <div>
              <p className="font-heading text-xl font-semibold text-primary">{value}</p>
              <p className="text-xs text-muted-foreground font-body">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Pages list */}
      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
        {pages.map((page, i) => (
          <div key={page.id} className={`flex items-center gap-4 px-5 py-4 hover:bg-background/50 transition-colors ${i < pages.length - 1 ? 'border-b border-border/50' : ''}`} data-testid={`cms-page-${page.id}`}>
            <div className="w-9 h-9 rounded-xl bg-background border border-border flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-primary/50" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-heading font-semibold text-primary text-sm">{page.title}</span>
                <Badge className={page.published ? 'bg-green-100 text-green-700 text-xs' : 'bg-gray-100 text-gray-500 text-xs'}>
                  {page.published ? 'Publié' : 'Masqué'}
                </Badge>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground font-body">
                <span className="font-mono bg-muted/50 px-1.5 py-0.5 rounded">{page.slug}</span>
                <span>{page.sections.length} section{page.sections.length > 1 ? 's' : ''}</span>
                <span>Modifié le {new Date(page.lastEdit).toLocaleDateString('fr-FR')}</span>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => togglePublish(page.id)} className="p-2 hover:bg-muted rounded-xl transition-colors" title={page.published ? 'Masquer' : 'Publier'}>
                {page.published ? <EyeOff className="w-4 h-4 text-muted-foreground" /> : <Eye className="w-4 h-4 text-green-600" />}
              </button>
              <Button size="sm" variant="outline" onClick={() => startEdit(page)} className="rounded-full text-xs font-body gap-1" data-testid={`cms-edit-${page.id}`}>
                <Edit className="w-3 h-3" /> Éditer
              </Button>
              <button onClick={() => removePage(page.id)} className="p-2 hover:bg-red-50 rounded-xl transition-colors" title="Supprimer" data-testid={`cms-delete-${page.id}`}>
                <Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
              </button>
            </div>
          </div>
        ))}
        {pages.length === 0 && (
          <div className="text-center py-10 text-muted-foreground font-body text-sm">Aucune page</div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <Dialog open={!!editing} onOpenChange={() => { if (!dirty || window.confirm('Quitter sans sauvegarder ?')) setEditing(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="font-heading text-lg">{editing.title}</DialogTitle>
                {dirty && <Badge className="bg-orange-100 text-orange-700 text-xs">Non sauvegardé</Badge>}
              </div>
            </DialogHeader>
            <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
              {editing.sections.map(section => (
                <div key={section.key}>
                  <label className="text-xs font-body font-semibold text-muted-foreground mb-1 block uppercase tracking-wide">{section.label}</label>
                  {editValues[section.key]?.length > 60 ? (
                    <Textarea
                      value={editValues[section.key]}
                      onChange={e => { setEditValues({ ...editValues, [section.key]: e.target.value }); setDirty(true); }}
                      className="font-body text-sm min-h-[80px] resize-none"
                      data-testid={`cms-field-${section.key}`}
                    />
                  ) : (
                    <Input
                      value={editValues[section.key]}
                      onChange={e => { setEditValues({ ...editValues, [section.key]: e.target.value }); setDirty(true); }}
                      className="font-body text-sm"
                      data-testid={`cms-field-${section.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="outline" onClick={() => setEditing(null)} className="font-body rounded-full gap-1">
                <X className="w-3.5 h-3.5" /> Annuler
              </Button>
              <Button onClick={saveEdit} className="bg-accent hover:bg-accent/90 text-white font-body rounded-full gap-1" data-testid="cms-save-btn">
                <Save className="w-3.5 h-3.5" /> Sauvegarder
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
